import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { PauseCircle, LifeBuoy, Send, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export function PausedScreen() {
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef();

  const load = async () => { try { const { data } = await api.get("/support/inbox"); setMessages(data.messages || []); } catch {} };
  useEffect(() => { if (open) load(); }, [open]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if (!input.trim()) return;
    setSending(true);
    try {
      await api.post("/support/message", { text: input.trim() });
      setInput(""); await load(); toast.success("Message sent to support");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen px-7 py-14 relative z-10 grid place-items-center text-center" data-testid="paused-screen">
      <div className="w-full">
        <div className="h-16 w-16 rounded-2xl grid place-items-center mx-auto mb-4" style={{ background: "color-mix(in srgb, var(--accent) 14%, transparent)" }}>
          <PauseCircle size={30} className="gold-text" />
        </div>
        <h1 className="font-serif-display text-3xl">Account paused</h1>
        <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
          Your account has been paused. Please contact our support team.
        </p>
        <motion.button whileTap={{ scale: 0.97 }} onClick={() => setOpen(true)} data-testid="paused-message-support-btn"
          className="w-full mt-7 rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
          style={{ background: "var(--accent)", color: "#fff" }}>
          <LifeBuoy size={16} /> Message Support
        </motion.button>
        <button onClick={logout} className="mt-4 text-sm" style={{ color: "var(--text-muted)" }} data-testid="paused-logout-btn">Log out</button>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" style={{ background: "rgba(0,0,0,0.5)" }}>
          <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            className="w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 flex flex-col" style={{ background: "var(--bg-surface)", height: "78dvh" }} data-testid="support-chat">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif-display text-2xl">Vault Support</h2>
              <button onClick={() => setOpen(false)}><X size={22} /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 rounded-xl p-3" style={{ background: "var(--overlay)" }}>
              {messages.length === 0 && <div className="text-sm text-center py-6" style={{ color: "var(--text-muted)" }}>Send us a message and our team will get back to you here.</div>}
              {messages.map((m) => (
                <div key={m.id} className={`flex ${m.direction === "to_support" ? "justify-end" : "justify-start"}`}>
                  <div className="rounded-2xl px-4 py-2.5 text-sm max-w-[80%] text-left whitespace-pre-wrap"
                    style={m.direction === "to_support" ? { background: "var(--accent)", color: "#fff" } : { background: "var(--bg-surface)", border: "1px solid var(--border)" }}>
                    {m.direction === "from_support" && <div className="text-[10px] gold-text mb-0.5">Vault Support</div>}
                    {m.text}
                  </div>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="flex items-center gap-2 mt-3">
              <textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2}
                placeholder="Type your message… (click send)" data-testid="support-input"
                className="flex-1 rounded-xl px-4 py-3 text-sm outline-none resize-none" style={{ background: "var(--overlay)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 16 }} />
              <button onClick={send} disabled={sending} data-testid="support-send-btn" className="h-11 w-11 rounded-xl grid place-items-center" style={{ background: "var(--accent)", color: "#fff" }}>
                {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
